import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, categories } from '@/mocks/data';

function Breadcrumb({ items }: { items: { label: string; to?: string }[] }) {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap">
      {items.map((item, idx) => (
        <span key={idx} className="flex items-center gap-2">
          {idx > 0 && (
            <div className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </div>
          )}
          {item.to ? (
            <Link to={item.to} className="hover:text-primary-500 transition-colors cursor-pointer">
              {item.label}
            </Link>
          ) : (
            <span className="text-foreground-900 font-medium">{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '1', 10);
  const article = articles.find((a) => a.id === articleId);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-24 pb-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-24 pb-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '分类浏览', to: '/category/hot' },
              { label: category?.name || '', to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="text-xs px-2.5 py-1 bg-primary-100 text-primary-700 rounded-full font-medium">
                {category?.name}
              </span>
              <span className="text-sm text-foreground-500">{article.date}</span>
              <span className="text-sm text-foreground-500 flex items-center gap-1">
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-eye-line"></i>
                </div>
                {article.views.toLocaleString()} 次浏览
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              {article.description}
            </p>
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              {article.tags.map((tag, idx) => (
                <span
                  key={idx}
                  className="text-xs px-2.5 py-1 bg-background-100 text-foreground-600 rounded-full border border-background-200/70"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </header>

          <div className="relative w-full h-[300px] md:h-[450px] rounded-lg overflow-hidden mb-10">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 撸撸社官网`}
              className="w-full h-full object-cover object-center"
            />
          </div>

          <div className="prose prose-lg max-w-none">
            <div
              className="text-foreground-800 leading-relaxed space-y-4"
              dangerouslySetInnerHTML={{
                __html: article.content
                  .replace(/^### (.+)$/gm, '<h3 class="text-lg font-semibold text-foreground-900 mt-8 mb-3">$1</h3>')
                  .replace(/^## (.+)$/gm, '<h2 class="text-xl font-heading font-bold text-foreground-900 mt-8 mb-4">$1</h2>')
                  .replace(/^- (.+)$/gm, '<li class="text-sm text-foreground-700 ml-4 mb-2">$1</li>')
                  .replace(/(\d+)\. (.+)/g, '<li class="text-sm text-foreground-700 ml-4 mb-1"><strong>$1.</strong> $2</li>')
                  .replace(/\*\*(.+?)\*\*/g, '<strong class="text-foreground-900">$1</strong>')
                  .replace(/---/g, '<hr class="my-8 border-background-200" />')
                  .replace(/\n\n/g, '<br/><br/>')
                  .replace(/\n/g, '<br/>'),
              }}
            />
          </div>

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div>
                <h3 className="text-sm font-medium text-foreground-500">文章标签</h3>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {article.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="text-xs px-3 py-1.5 bg-background-100 text-foreground-600 rounded-full border border-background-200/70"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
              <Link
                to={`/category/${article.category}`}
                className="flex items-center gap-1 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                返回{category?.name}
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="max-w-4xl mx-auto px-4 md:px-6 mt-12">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">相关推荐</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/article/${rel.id}`}
                  className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200/50 transition-all duration-300 group cursor-pointer"
                >
                  <div className="relative w-full h-40 overflow-hidden">
                    <img
                      src={rel.image}
                      alt={rel.title}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors">
                      {rel.title}
                    </h3>
                    <span className="text-xs text-foreground-500 mt-2 block">{rel.date}</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}